import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase.config';
import { addDoc, collection, doc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import {
  FiArrowLeft,
  FiUpload,
  FiTrash2,
  FiCheck,
  FiX,
  FiImage,
  FiStar,
  FiInfo
} from 'react-icons/fi';
import { MdStorefront } from 'react-icons/md';
import { cities } from '../data/citiesData';
import { carsData } from '../data/carsData';

const AddCarToShowroom = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for form data
  const [newListingData, setNewListingData] = useState({
    manufacturer: '',
    modelName: '',
    offer: false,
    transmission: 'Automatic',
    modelYear: '2022',
    color: '',
    mileage: '',
    price: '',
    discountedPrice: '',
    notes: '',
    ranking: 0,
    carImages: [],
    primaryImage: '',
    options: {
      leatherSeats: false,
      airBags: false,
      bluetooth: false,
      fogLights: false,
      fullOption: false,
    },
    phoneNumber: '',
    location: '',
    city: '',
  });

  const [sellerId, setSellerId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showroomData, setShowroomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedManufacturer, setSelectedManufacturer] = useState('');

  // Fetch the user's showroom
  useEffect(() => {
    const fetchUserShowroom = async () => {
      if (!currentUser) {
        toast.error('Please login to add a listing');
        navigate('/sign-in');
        return;
      }

      try {
        const sellersRef = collection(db, 'Feature-Seller');
        const q = query(sellersRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          toast.error('You need to register a showroom first');
          navigate('/register-showroom');
          return;
        }

        const sellerDoc = querySnapshot.docs[0];
        const sellerData = sellerDoc.data();
        const sellerId = sellerDoc.id;
        
        setSellerId(sellerId);
        setShowroomData(sellerData);
        
        // Pre-fill form with seller data
        setNewListingData((prevData) => ({
          ...prevData,
          phoneNumber: sellerData.phoneNumber || '',
          location: sellerData.address || '',
          city: sellerData.city || '',
        }));
        
        setLoading(false);
      } catch (error) {
        toast.error(`Failed to fetch showroom: ${error.message}`);
        setLoading(false);
      }
    };

    fetchUserShowroom();
  }, [currentUser, navigate]);

  // Handle manufacturer change
  const handleManufacturerChange = (e) => {
    const manufacturer = e.target.value;
    setSelectedManufacturer(manufacturer);
    setNewListingData((prevData) => ({
      ...prevData,
      manufacturer: manufacturer,
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewListingData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setNewListingData((prevData) => ({
      ...prevData,
      options: {
        ...prevData.options,
        [name]: checked,
      },
    }));
  };

  // Handle image uploads
  const handleImageChange = (e) => {
    const files = e.target.files;
    if (files.length === 0) return;
    
    // Check if adding these files would exceed 6 images
    if (newListingData.carImages.length + files.length > 6) {
      toast.error('Maximum 6 images allowed. You can upload ' + (6 - newListingData.carImages.length) + ' more.');
      return;
    }

    setUploadingImages(true);
    let uploadedCount = 0;
    const totalFiles = files.length;

    Array.from(files).forEach((file) => {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 5MB.`);
        setUploadingImages(false);
        return;
      }

      const uniqueName = `${Date.now()}-${file.name}`;
      const imageRef = ref(storage, `carImages/${sellerId}/${uniqueName}`);
      const uploadTask = uploadBytesResumable(imageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setProgress(progress);
        },
        (error) => {
          toast.error(`Image upload failed: ${error.message}`);
          setUploadingImages(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setNewListingData((prevData) => ({
              ...prevData,
              carImages: [...prevData.carImages, downloadURL],
            }));
            
            uploadedCount++;
            if (uploadedCount === totalFiles) {
              setUploadingImages(false);
              setProgress(0);
              toast.success('All images uploaded successfully!');
            }
          } catch (error) {
            toast.error(`Error fetching image URL: ${error.message}`);
            setUploadingImages(false);
          }
        }
      );
    });
  };

  // Remove image
  const handleRemoveImage = (index) => {
    setNewListingData((prevData) => {
      const updatedImages = prevData.carImages.filter((_, i) => i !== index);
      return {
        ...prevData,
        carImages: updatedImages,
        primaryImage: prevData.primaryImage === prevData.carImages[index] 
          ? (updatedImages[0] || '') 
          : prevData.primaryImage
      };
    });
  };

  // Set primary image
  const handleSetPrimaryImage = (image) => {
    setNewListingData((prevData) => ({
      ...prevData,
      primaryImage: image
    }));
    toast.success('Primary image set!');
  };

  // Handle form submission
  const handleAddListing = async (e) => {
    e.preventDefault();
    
    if (!sellerId) {
      toast.error('No showroom found. Please register a showroom first.');
      return;
    }

    if (newListingData.carImages.length === 0) {
      toast.error('Please upload at least one image.');
      return;
    }

    if (!newListingData.price || newListingData.price <= 0) {
      toast.error('Please enter a valid price.');
      return;
    }

    if (!newListingData.manufacturer) {
      toast.error('Please select a manufacturer.');
      return;
    }

    if (!newListingData.modelName) {
      toast.error('Please enter a model name.');
      return;
    }

    setIsSubmitting(true);

    try {
      const listingsRef = collection(db, 'Feature-Seller', sellerId, 'listings');
      
      // Set primary image (use first image if not set)
      const primaryImage = newListingData.primaryImage || newListingData.carImages[0];

      const listingData = {
        ...newListingData,
        primaryImage: primaryImage,
        userRef: sellerId,
        createdAt: new Date().toISOString(),
        status: 'active'
      };

      await addDoc(listingsRef, listingData);

      toast.success('🎉 Listing added successfully!');

      // Reset form
      setNewListingData({
        manufacturer: '',
        modelName: '',
        offer: false,
        transmission: 'Automatic',
        modelYear: '2022',
        color: '',
        mileage: '',
        price: '',
        discountedPrice: '',
        notes: '',
        ranking: 0,
        carImages: [],
        primaryImage: '',
        options: {
          leatherSeats: false,
          airBags: false,
          bluetooth: false,
          fogLights: false,
          fullOption: false,
        },
        phoneNumber: showroomData?.phoneNumber || '',
        location: showroomData?.address || '',
        city: showroomData?.city || '',
      });
      setSelectedManufacturer('');

      // Navigate back to showroom profile
      setTimeout(() => {
        navigate(`/seller-profile/${sellerId}`);
      }, 1000);
      
    } catch (error) {
      toast.error(`Failed to add listing: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get unique manufacturers from carsData
  const manufacturers = [...new Set(carsData.map(car => car.manufacturer))].sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading showroom data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(`/seller-profile/${sellerId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
          >
            <FiArrowLeft className="text-lg" />
            <span>Back to Showroom</span>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-700 font-medium">
                {showroomData?.businessName || 'Showroom'}
              </span>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <MdStorefront className="text-2xl" />
              Add New Listing
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Add a new vehicle to your showroom inventory
            </p>
          </div>

          <form onSubmit={handleAddListing} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Manufacturer - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturer *
                  </label>
                  <select
                    name="manufacturer"
                    value={newListingData.manufacturer}
                    onChange={handleManufacturerChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  >
                    <option value="">Select Manufacturer</option>
                    {manufacturers.map((manufacturer) => (
                      <option key={manufacturer} value={manufacturer}>
                        {manufacturer}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Model Name - Text Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model Name *
                  </label>
                  <input
                    type="text"
                    name="modelName"
                    value={newListingData.modelName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                    placeholder="e.g., Corolla, Civic, X5, Mustang"
                  />
                  {selectedManufacturer && (
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the model name for {selectedManufacturer}
                    </p>
                  )}
                </div>

                {/* Model Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model Year *
                  </label>
                  <input
                    type="number"
                    name="modelYear"
                    value={newListingData.modelYear}
                    onChange={handleInputChange}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  />
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={newListingData.color}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="e.g., White, Black, Silver"
                  />
                </div>

                {/* Transmission */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transmission *
                  </label>
                  <select
                    name="transmission"
                    value={newListingData.transmission}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  >
                    <option value="Automatic">Automatic</option>
                    <option value="Manual">Manual</option>
                  </select>
                </div>

                {/* Mileage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mileage (km)
                  </label>
                  <input
                    type="number"
                    name="mileage"
                    value={newListingData.mileage}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="e.g., 50000"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (Rs.) *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={newListingData.price}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                    placeholder="e.g., 2500000"
                  />
                </div>

                {/* Discounted Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discounted Price (Optional)
                  </label>
                  <input
                    type="number"
                    name="discountedPrice"
                    value={newListingData.discountedPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="e.g., 2200000"
                  />
                </div>

                {/* City - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <select
                    name="city"
                    value={newListingData.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  >
                    <option value="">Select City</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.city}>
                        {city.city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location / Address
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={newListingData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="Full address"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={newListingData.phoneNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                    placeholder="+92-XXX-XXXXXXX"
                  />
                </div>

                {/* Ranking */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ranking (1-5) *
                  </label>
                  <select
                    name="ranking"
                    value={newListingData.ranking}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  >
                    <option value="0">Select ranking</option>
                    <option value="1">⭐ 1 Star</option>
                    <option value="2">⭐⭐ 2 Stars</option>
                    <option value="3">⭐⭐⭐ 3 Stars</option>
                    <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                    <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes / Description
              </label>
              <textarea
                name="notes"
                value={newListingData.notes}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-none"
                placeholder="Describe the vehicle's condition, features, etc."
              />
            </div>

            {/* Options */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle Options
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.keys(newListingData.options).map((option) => (
                  <label key={option} className="flex items-center gap-2 p-2 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition">
                    <input
                      type="checkbox"
                      name={option}
                      checked={newListingData.options[option]}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm capitalize">{option.replace(/([A-Z])/g, ' $1').trim()}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Image Upload */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Images (Max 6)
              </label>
              
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 transition bg-gray-50">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                  </div>
                  <input
                    type="file"
                    name="carImages"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    disabled={uploadingImages}
                  />
                </label>
              </div>

              {uploadingImages && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Uploading...</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Image Previews */}
              {newListingData.carImages.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Uploaded Images ({newListingData.carImages.length}/6)
                    <span className="text-xs text-gray-500 ml-2">(Click to set as primary)</span>
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {newListingData.carImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          className={`w-full h-24 object-cover rounded-xl cursor-pointer border-2 transition ${
                            newListingData.primaryImage === image
                              ? 'border-blue-500 ring-2 ring-blue-300'
                              : 'border-gray-200 hover:border-blue-400'
                          }`}
                          onClick={() => handleSetPrimaryImage(image)}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition shadow-lg"
                        >
                          <FiX className="text-xs" />
                        </button>
                        {newListingData.primaryImage === image && (
                          <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs text-center py-0.5 rounded-b-xl">
                            Primary
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex gap-4">
              <button
                type="button"
                onClick={() => navigate(`/seller-profile/${sellerId}`)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || uploadingImages || newListingData.carImages.length === 0}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FiCheck className="text-lg" />
                    Add Listing
                  </>
                )}
              </button>
            </div>

            {newListingData.carImages.length === 0 && !uploadingImages && (
              <p className="text-center text-sm text-amber-600 mt-2">
                ⚠️ Please upload at least one image before submitting
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCarToShowroom;